import type { Fulfilment } from '@felix/domain';
import { IconPointFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { PO_TONE } from './orders.js';
import classes from './Purchasing.module.css';
import { formatAmount, formatQuantity } from './requisitions.js';
import directory from './SupplierDirectory.module.css';

/**
 * Talep kartında "Orders by Supplier" (ADR 0115; ADR 0111 §E): talebin verilmiş PO'ları
 * tedarikçiye göre gruplu (bölünmüş liste); her PO'nun kalemleri, istenen ve teslim alınan
 * miktar, birim fiyat. İkame satırı "Substitute" rozetli ve yerine geçtiği kalemin adıyla; stoğa
 * girmez. Gemide fiyat ve PO toplamı yok, tedarikçi adı var (sahip kararı 2026-11-05; ADR 0169 §2).
 */
export function OrdersBySupplier({
  fulfilment,
  prices = true,
}: {
  fulfilment: Fulfilment | undefined;
  prices?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasingShip.bySupplier.${key}`, options);
  const groups = fulfilment?.bySupplier ?? [];
  if (!fulfilment?.hasOrders || groups.length === 0) {
    return <EmptyState title={k('empty')} description={k('emptyHint')} />;
  }
  return (
    <div className={directory.groups}>
      {groups.map((group) => {
        const lines = group.orders.reduce((sum, order) => sum + order.lines.length, 0);
        return (
          <section
            key={group.supplierId ?? group.supplierName}
            className={directory.group}
            aria-label={group.supplierName}
          >
            <header className={directory.groupHead}>
              <h3 className={directory.groupTitle}>{group.supplierName}</h3>
              <span className={directory.groupMeta}>
                {k('meta', { count: group.orders.length, lines })}
              </span>
            </header>
            {/* Geniş sipariş tablosu dar pencerede bilerek kayar (klavyeyle; WCAG 2.1.1) */}
            <section
              className={table.tableWrap}
              data-allow-scroll
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
              aria-label={k('table', { name: group.supplierName })}
            >
              <table className={`${table.table} ${classes.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{k('columns.po')}</th>
                    <th scope="col" className={classes.key}>
                      {k('columns.item')}
                    </th>
                    <th scope="col">{t('purchasing.form.columns.uom')}</th>
                    <th scope="col" data-numeric>
                      {k('columns.ordered')}
                    </th>
                    <th scope="col" data-numeric>
                      {k('columns.received')}
                    </th>
                    {prices ? (
                      <>
                        <th scope="col" data-numeric>
                          {k('columns.unitPrice')}
                        </th>
                        <th scope="col" data-numeric>
                          {k('columns.total')}
                        </th>
                      </>
                    ) : null}
                    <th scope="col">{k('columns.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {group.orders.flatMap((order) =>
                    order.lines.map((line, index) => (
                      <tr key={line.id}>
                        <td>{index === 0 ? <strong>{order.poNo}</strong> : null}</td>
                        <td className={`${classes.material} ${classes.key}`}>
                          {line.name}
                          {line.substitute ? (
                            <>
                              {' '}
                              <StatusBadge tone="warning">{k('substitute')}</StatusBadge>
                              {line.substituteFor ? (
                                <span className={directory.substituteFor}>
                                  {k('substituteFor', { name: line.substituteFor })}
                                </span>
                              ) : null}
                            </>
                          ) : null}
                        </td>
                        <td>{line.unit}</td>
                        <td data-numeric>{formatQuantity(line.quantity, locale)}</td>
                        <td data-numeric>{formatQuantity(line.accepted, locale)}</td>
                        {prices ? (
                          <>
                            <td data-numeric>
                              {`${formatAmount(line.unitPrice, locale)} ${order.currency}`}
                            </td>
                            <td data-numeric>
                              {index === 0
                                ? `${formatAmount(order.total, locale)} ${order.currency}`
                                : null}
                            </td>
                          </>
                        ) : null}
                        <td>
                          {index === 0 ? (
                            <StatusBadge
                              tone={PO_TONE[order.status]}
                              icon={<IconPointFilled size={10} />}
                            >
                              {t(`purchasing.po.statuses.${order.status}`)}
                            </StatusBadge>
                          ) : null}
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
            </section>
          </section>
        );
      })}
      {prices ? null : <p className={classes.note}>{t('purchasingShip.prices.hidden')}</p>}
    </div>
  );
}
